import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@praetorian-chariot/ui';
import React from 'react';

import { Tooltip } from '@/components/Tooltip';
import { useForcePlexTracSync, usePlexTracClient } from '@/hooks/usePlextrac';
import {
  PlexTracClientId,
  ReportingSettingsSection,
} from '@/sections/settings/components';
import { cn } from '@/utils/classname';

interface PlexTracReportingProps {
  customerEmail?: string;
  isPraetorianUser?: boolean;
  className?: string;
  showTitle?: boolean;
}

export const PlexTracReporting: React.FC<PlexTracReportingProps> = ({
  customerEmail,
  isPraetorianUser = false,
  className,
  showTitle = true,
}) => {
  // Use customer context for PlexTrac client ID - each customer has their own PlexTrac client
  const { clientId } = usePlexTracClient(customerEmail);
  const hasValidClientId = Boolean(clientId && clientId.trim() !== '');
  const { mutateAsync: forceSync, status: forceSyncStatus } =
    useForcePlexTracSync(customerEmail);
  const isForceSyncing = forceSyncStatus === 'pending';

  // Unified layout for all use cases
  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {showTitle && (
        <div>
          <h2 className="text-chariot-text-primary text-lg font-bold">
            Reporting
          </h2>
          <p className="text-chariot-text-secondary text-sm">
            Configure PlexTrac integration and manage report exports.
          </p>
        </div>
      )}

      {/* When integrated in Praetorian section, show section title */}
      {!showTitle && (
        <div>
          <h3 className="text-chariot-text-primary text-base font-bold">
            Reporting
          </h3>
          <p className="text-chariot-text-secondary text-sm">
            Configure PlexTrac integration and manage report exports.
          </p>
        </div>
      )}

      <div
        className={
          showTitle
            ? 'border-chariot-stroke bg-chariot-background-primary rounded-lg border'
            : ''
        }
      >
        <div className={showTitle ? 'space-y-6 p-6' : 'space-y-6'}>
          {/* PlexTrac Client ID Configuration */}
          <div className="border-chariot-stroke rounded-md border p-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-2">
                <h3 className="text-chariot-text-primary text-base font-semibold">
                  PlexTrac Configuration
                </h3>
                <p className="text-chariot-text-secondary text-sm">
                  Configure your PlexTrac Client ID for report integration.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <PlexTracClientId
                  customerEmail={customerEmail}
                  isPraetorianUser={isPraetorianUser}
                  className=""
                />
                <Tooltip title="Force-syncs your account with PlexTrac, importing all findings from all connected reports">
                  <span>
                    <Button
                      label={isForceSyncing ? 'Syncing...' : 'Force Sync'}
                      styleType="secondary"
                      size="sm"
                      onClick={() => forceSync()}
                      disabled={
                        !isPraetorianUser || !hasValidClientId || isForceSyncing
                      }
                    />
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Report Management - Only show when PlexTrac is configured */}
          {hasValidClientId && (
            <div className="border-chariot-stroke rounded-md border p-4">
              <div className="mb-4 flex items-center gap-2">
                <h3 className="text-chariot-text-primary text-base font-semibold">
                  Report Management & Sharing
                </h3>
                <div className="group relative">
                  <InformationCircleIcon className="text-chariot-text-secondary hover:text-chariot-text-primary size-5 cursor-help transition-colors" />
                  <div className="bg-chariot-background-primary border-chariot-stroke invisible absolute top-full left-0 z-25 mt-2 w-80 rounded-lg border p-3 shadow-lg group-hover:visible">
                    <p className="text-chariot-text-secondary text-xs leading-relaxed">
                      Selected reports are converted to PDF and automatically
                      shared with your customers. They&apos;ll receive
                      notifications and can access the reports in their
                      Documents section.
                    </p>
                  </div>
                </div>
              </div>
              <ReportingSettingsSection
                plexTracClientId={clientId}
                customerEmail={customerEmail}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
